// components/Modals/AddInvoiceModal.js
import { useState, useEffect } from 'react';
import { 
  X, 
  FileText,
  Calendar,
  User,
  Tag,
  Check,
  ChevronRight,
  Search
} from 'lucide-react';
import { toast } from 'react-toastify';
import api from '../../api/axios';
import { useTranslation } from '../../i18n/i18n';

const AddInvoiceModal = ({ 
  onClose, 
  onSuccess,
  lang 
}) => {
  const { t } = useTranslation(lang);
  const [step, setStep] = useState(1); // 1: Filtres, 2: Sélection client, 3: Sélection réservations, 4: Confirmation
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [customersList, setCustomersList] = useState([]); // Liste complète des clients
  
  // État pour l'étape 1
  const [filters, setFilters] = useState({
    customerId: '',
    service: '',
    dateType: 'created_at',
    dateFrom: '2024-01-01',
    dateTo: new Date().toISOString().split('T')[0]
  });
  
  // État pour l'étape 2
  const [customersSummary, setCustomersSummary] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [token, setToken] = useState('');
  
  // État pour l'étape 3
  const [bookings, setBookings] = useState([]);
  const [selectedBookings, setSelectedBookings] = useState([]);
  const [rateIds, setRateIds] = useState([]);
  const [detailToken, setDetailToken] = useState('');
  
  // État pour l'étape 4
  const [dueAt, setDueAt] = useState(() => {
    const date = new Date();
    date.setDate(date.getDate() + 30);
    return date.toISOString().split('T')[0];
  });

  const services = ['flight', 'hotel', 'package', 'transfer', 'activity'];

  // Charger la liste des clients au démarrage
  useEffect(() => {
    fetchCustomers();
  }, []);

  // Fonction pour charger la liste des clients
  const fetchCustomers = async () => {
    try {
      const response = await api.get('/customers');      
      if (response.data && response.data.result) {
        setCustomersList(response.data.result.data || []);
      }
    } catch (error) {
      console.error('Erreur lors du chargement des clients:', error);
    }
  };

  // Étape 1: Rechercher les clients
  const handleSearch = async () => {
    if (!filters.dateFrom || !filters.dateTo) {
      toast.error(t('pleaseSelectDates'));
      return;
    }

    setLoading(true);
    try {
      const url_invoice = process.env.REACT_APP_API_INVOICE_URL;
      const response = await api.post(`${url_invoice}/customer/new`, {
        customerId: filters.customerId || '',
        service: filters.service || '',
        dateType: filters.dateType,
        dateFrom: filters.dateFrom,
        dateTo: filters.dateTo
      });
      
      if (response.data && response.data.result) {
        setToken(response.data.result.token);
        setCustomersSummary(response.data.result.list);
        
        // Si un seul client, passer directement à l'étape suivante
        if (response.data.result.list.length === 1) {
          setSelectedCustomer(response.data.result.list[0]);
          handleCustomerSelect(response.data.result.list[0]);
        } else if (response.data.result.list.length > 0) {
          setStep(2);
          toast.success(`${response.data.result.list.length} ${t('customersFound')}`);
        } else {
          toast.info(t('noCustomersFound'));
        }
      }
    } catch (error) {
      console.error('Erreur lors de la recherche:', error);
      toast.error(error.response?.data?.message || t('searchError'));
    } finally {
      setLoading(false);
    }
  };

  // Étape 2: Sélectionner un client
  const handleCustomerSelect = async (customer) => {
    setSelectedCustomer(customer);
    setLoading(true);
    
    try {
      const url_invoice = process.env.REACT_APP_API_INVOICE_URL;
      const response = await api.post(`${url_invoice}/customer/new/detail`, {
        token: token,
        customerId: customer.customerId,
        currency: customer.currency
      });
      
      if (response.data && response.data.result) {
        setDetailToken(response.data.result.token);
        setBookings(response.data.result.list);
        setRateIds(response.data.result.rateIds);
        setStep(3);
        toast.success(`${response.data.result.list.length} ${t('bookingsFound')}`);
      }
    } catch (error) {
      console.error('Erreur lors du chargement des réservations:', error);
      toast.error(error.response?.data?.message || t('errorLoadingBookings'));
    } finally {
      setLoading(false);
    }
  };

  // Étape 3: Sélectionner les réservations
  const toggleBookingSelection = (booking) => {
    const isSelected = selectedBookings.some(b => b.booking_id === booking.booking_id);
    
    if (isSelected) {
      setSelectedBookings(prev => prev.filter(b => b.booking_id !== booking.booking_id));
    } else {
      setSelectedBookings(prev => [...prev, booking]);
    }
  };

  const selectAllBookings = () => {
    if (selectedBookings.length === bookings.length) {
      setSelectedBookings([]);
    } else {
      setSelectedBookings([...bookings]);
    }
  };

  // Étape 4: Créer la facture
  const handleCreateInvoice = async () => {
    if (selectedBookings.length === 0) {
      toast.error(t('selectAtLeastOneBooking'));
      return;
    }

    if (!dueAt) {
      toast.error(t('selectDueDate'));
      return;
    }

    setSubmitting(true);
    try {
      const url_invoice = process.env.REACT_APP_API_INVOICE_URL;
      const selectedRateIds = selectedBookings.map(booking => booking.rate_id);
      
      const response = await api.post(`${url_invoice}/customer/new/save`, {
        token: detailToken,
        dueAt: dueAt,
        rateIds: selectedRateIds
      });
      
      if (response.data && response.data.result) {        
        // Afficher le résultat
        const result = response.data.result;
        toast.info(
          <div>
            <p><strong>{t('invoiceCreated')}</strong></p>
            <p>{t('invoiceNumber')}: {result.invoice_number}</p>
            <p>{t('amount')}: {result.amount} {result.currency}</p>
            <p>ID: {result.invoice_id}</p>
          </div>,
          {
            autoClose: 5000,
            closeOnClick: false
          }
        );
        
        onSuccess(result);
        onClose();
      }
    } catch (error) {
      console.error('Erreur lors de la création:', error);
      toast.error(error.response?.data?.message || t('errorCreatingInvoice'));
    } finally {
      setSubmitting(false);
    }
  };

  // Calcul du total
  const calculateTotalAmount = () => {
    return selectedBookings.reduce((total, booking) => {
      return total + (parseFloat(booking.amount) || 0);
    }, 0).toFixed(2);
  };

  // Styles
  const styles = {
    modalOverlay: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 1000,
      padding: '20px'
    },
    modalContent: {
      background: 'white',
      borderRadius: '12px',
  width: '95%',
  maxWidth: '1100px',
  maxHeight: '95vh',
      overflow: 'hidden',
      boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
      display: 'flex',
      flexDirection: 'column'
    },
    modalHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: '24px',
      borderBottom: '1px solid #e5e7eb',
      background: '#f8fafc'
    },
    modalTitle: {
      fontSize: '20px',
      fontWeight: '600',
      color: '#1f2937',
      margin: 0,
      display: 'flex',
      alignItems: 'center',
      gap: '10px'
    },
    closeButton: {
      background: 'transparent',
      border: 'none',
      cursor: 'pointer',
      color: '#6b7280',
      borderRadius: '6px',
      padding: '4px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      transition: 'all 0.2s ease',
      '&:hover': {
        background: '#f3f4f6',
        color: '#374151'
      }
    },
    modalBody: {
      padding: '24px',
      overflowY: 'auto',
      flex: 1
    },
    stepIndicator: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '32px',
      position: 'relative'
    },
    step: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      flex: 1,
      position: 'relative',
      zIndex: 2
    },
    stepCircle: {
      width: '36px',
      height: '36px',
      borderRadius: '50%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontWeight: '600',
      fontSize: '14px',
      marginBottom: '8px',
      transition: 'all 0.3s ease'
    },
    stepActive: {
      background: '#7c3aed',
      color: 'white'
    },
    stepCompleted: {
      background: '#10b981',
      color: 'white'
    },
    stepInactive: {
      background: '#e5e7eb',
      color: '#6b7280'
    },
    stepLabel: {
      fontSize: '12px',
      fontWeight: '500',
      color: '#6b7280',
      textAlign: 'center'
    },
    stepLabelActive: {
      color: '#7c3aed',
      fontWeight: '600'
    },
    stepLine: {
      position: 'absolute',
      top: '18px',
      left: '10%',
      right: '10%',
      height: '2px',
      background: '#e5e7eb',
      zIndex: 1
    },
    stepLineActive: {
      background: '#7c3aed'
    },
    formGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
      gap: '16px',
      marginBottom: '24px'
    },
    formGroup: {
      display: 'flex',
      flexDirection: 'column',
      gap: '8px'
    },
    label: {
      fontSize: '14px',
      fontWeight: '600',
      color: '#374151',
      display: 'flex',
      alignItems: 'center',
      gap: '6px'
    },
    input: {
      padding: '10px 12px',
      border: '1px solid #d1d5db',
      borderRadius: '6px',
      fontSize: '14px',
      transition: 'all 0.2s ease',
      '&:focus': {
        outline: 'none',
        borderColor: '#7c3aed',
        boxShadow: '0 0 0 3px rgba(124, 58, 237, 0.1)'
      }
    },
    select: {
      padding: '10px 12px',
      border: '1px solid #d1d5db',
      borderRadius: '6px',
      fontSize: '14px',
      background: 'white',
      transition: 'all 0.2s ease',
      '&:focus': {
        outline: 'none',
        borderColor: '#7c3aed',
        boxShadow: '0 0 0 3px rgba(124, 58, 237, 0.1)'
      }
    },
    searchButton: {
      padding: '12px 24px',
      background: '#7c3aed',
      color: 'white',
      border: 'none',
      borderRadius: '8px',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '8px',
      fontWeight: '600',
      fontSize: '16px',
      width: '100%',
      transition: 'all 0.2s ease',
      '&:hover': {
        background: '#6d28d9'
      },
      '&:disabled': {
        opacity: '0.6',
        cursor: 'not-allowed'
      }
    },
    customersList: {
      display: 'grid',
      gap: '12px',
      marginTop: '16px'
    },
    customerCard: {
      padding: '16px',
      border: '2px solid #e5e7eb',
      borderRadius: '8px',
      cursor: 'pointer',
      transition: 'all 0.2s ease',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      '&:hover': {
        borderColor: '#7c3aed',
        background: '#faf5ff',
        transform: 'translateY(-2px)'
      }
    },
    customerSelected: {
      borderColor: '#7c3aed',
      background: '#faf5ff',
      boxShadow: '0 4px 12px rgba(124, 58, 237, 0.1)'
    },
    customerInfo: {
      display: 'flex',
      flexDirection: 'column',
      gap: '4px'
    },
    customerName: {
      fontWeight: '600',
      color: '#1f2937',
      fontSize: '15px'
    },
    customerDetails: {
      display: 'flex',
      gap: '12px',
      fontSize: '13px',
      color: '#6b7280'
    },
    customerAmount: {
      fontSize: '18px',
      fontWeight: 'bold',
      color: '#10b981'
    },
    bookingsContainer: {
      maxHeight: '400px',
      overflowY: 'auto',
      border: '1px solid #e5e7eb',
      borderRadius: '8px',
      marginBottom: '24px'
    },
    bookingItem: {
      display: 'flex',
      alignItems: 'center',
      padding: '12px 16px',
      borderBottom: '1px solid #e5e7eb',
      transition: 'all 0.2s ease',
      cursor: 'pointer',
      '&:hover': {
        background: '#f9fafb'
      },
      '&:last-child': {
        borderBottom: 'none'
      }
    },
    bookingSelected: {
      background: '#e0e7ff',
      borderLeft: '3px solid #7c3aed'
    },
    bookingInfo: {
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
      gap: '4px'
    },
    bookingNumber: {
      fontWeight: '600',
      color: '#1f2937'
    },
    bookingDetails: {
      display: 'flex',
      flexWrap: 'wrap',
      gap: '8px',
      fontSize: '12px',
      color: '#6b7280'
    },
    bookingAmount: {
      fontWeight: '600',
      color: '#10b981',
      minWidth: '80px',
      textAlign: 'right'
    },
    checkbox: {
      width: '18px',
      height: '18px',
      marginRight: '12px',
      cursor: 'pointer'
    },
    selectAllContainer: {
      display: 'flex',
      alignItems: 'center',
      padding: '12px 16px',
      background: '#f3f4f6',
      borderBottom: '1px solid #e5e7eb',
      fontWeight: '600',
      color: '#374151'
    },
    summaryCard: {
      background: '#f8fafc',
      padding: '20px',
      borderRadius: '8px',
      border: '1px solid #e5e7eb',
      marginBottom: '24px'
    },
    summaryGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
      gap: '16px',
      marginBottom: '20px'
    },
    summaryItem: {
      textAlign: 'center',
      padding: '12px',
      background: 'white',
      borderRadius: '6px',
      border: '1px solid #e5e7eb'
    },
    summaryValue: {
      fontSize: '20px',
      fontWeight: 'bold',
      marginBottom: '4px',
      color: '#1f2937'
    },
    summaryLabel: {
      fontSize: '12px',
      color: '#6b7280'
    },
    dueDateContainer: {
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      marginTop: '20px'
    },
    dueDateLabel: {
      fontWeight: '600',
      color: '#374151',
      minWidth: '100px'
    },
    modalFooter: {
      display: 'flex',
      justifyContent: 'space-between',
      gap: '12px',
      padding: '24px',
      borderTop: '1px solid #e5e7eb',
      background: '#f8fafc'
    },
    backButton: {
      padding: '10px 20px',
      background: 'transparent',
      color: '#4b5563',
      border: '1px solid #d1d5db',
      borderRadius: '6px',
      cursor: 'pointer',
      fontWeight: '500',
      transition: 'all 0.2s ease',
      '&:hover': {
        background: '#f3f4f6'
      }
    },
    nextButton: {
      padding: '10px 20px',
      background: '#7c3aed',
      color: 'white',
      border: 'none',
      borderRadius: '6px',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      fontWeight: '500',
      transition: 'all 0.2s ease',
      '&:hover': {
        background: '#6d28d9'
      }
    },
    createButton: {
      padding: '10px 20px',
      background: '#10b981',
      color: 'white',
      border: 'none',
      borderRadius: '6px',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      fontWeight: '500',
      transition: 'all 0.2s ease',
      '&:hover': {
        background: '#0da271'
      },
      '&:disabled': {
        opacity: '0.6',
        cursor: 'not-allowed'
      }
    },
    loadingContainer: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      padding: '40px'
    },
    loadingText: {
      color: '#6b7280',
      fontSize: '16px'
    },
    noData: {
      textAlign: 'center',
      padding: '40px 20px',
      color: '#6b7280',
      background: '#f9fafb',
      borderRadius: '8px',
      border: '2px dashed #d1d5db'
    },
    customerSelectContainer: {
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      marginBottom: '16px'
    },
    customerSearch: {
      flex: 1,
      position: 'relative'
    },
    searchInput: {
      width: '100%',
      padding: '10px 12px 10px 40px',
      border: '1px solid #d1d5db',
      borderRadius: '6px',
      fontSize: '14px',
      transition: 'all 0.2s ease',
      '&:focus': {
        outline: 'none',
        borderColor: '#7c3aed',
        boxShadow: '0 0 0 3px rgba(124, 58, 237, 0.1)'
      }
    },
    searchIcon: {
      position: 'absolute',
      left: '12px',
      top: '50%',
      transform: 'translateY(-50%)',
      color: '#9ca3af'
    },
    selectAllButton: {
      padding: '10px 16px',
      background: '#f3f4f6',
      color: '#374151',
      border: '1px solid #d1d5db',
      borderRadius: '6px',
      cursor: 'pointer',
      fontWeight: '500',
      fontSize: '14px',
      transition: 'all 0.2s ease',
      '&:hover': {
        background: '#e5e7eb'
      }
    }
  };

  const renderStepContent = () => {
    switch(step) {
      case 1:
        return (
          <div>
            <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#1f2937', marginBottom: '20px' }}>
              {t('configureSearchFilters')}
            </h3>
            
            <div style={styles.formGrid}>
              <div style={styles.formGroup}>
                <label style={styles.label}>
                  <User size={16} />
                  {t('customer')}
                </label>
                <select
                  style={styles.select}
                  value={filters.customerId}
                  onChange={(e) => setFilters({...filters, customerId: e.target.value})}
                >
                  <option value="">{t('allCustomers')}</option>
                  {customersList.map(customer => (
                    <option key={customer.id} value={customer.id}>
                      {customer.name} {customer.company ? `(${customer.company})` : ''}
                    </option>
                  ))}
                </select>
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>
                  <Tag size={16} />
                  {t('service')}
                </label>
                <select
                  style={styles.select}
                  value={filters.service}
                  onChange={(e) => setFilters({...filters, service: e.target.value})}
                >
                  <option value="">{t('allServices')}</option>
                  {services.map(service => (
                    <option key={service} value={service}>
                      {t(service)}
                    </option>
                  ))}
                </select>
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>
                  <Calendar size={16} />
                  {t('dateType')}
                </label>
                <select
                  style={styles.select}
                  value={filters.dateType}
                  onChange={(e) => setFilters({...filters, dateType: e.target.value})}
                >
                  <option value="created_at">{t('creationDate')}</option>
                  <option value="checkin">{t('checkinDate')}</option>
                  <option value="checkout">{t('checkoutDate')}</option>
                </select>
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>
                  <Calendar size={16} />
                  {t('dateFrom')}
                </label>
                <input
                  type="date"
                  style={styles.input}
                  value={filters.dateFrom}
                  onChange={(e) => setFilters({...filters, dateFrom: e.target.value})}
                  required
                />
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>
                  <Calendar size={16} />
                  {t('dateTo')}
                </label>
                <input
                  type="date"
                  style={styles.input}
                  value={filters.dateTo}
                  onChange={(e) => setFilters({...filters, dateTo: e.target.value})}
                  required
                />
              </div>
            </div>

          </div>
        );

      case 2:
        return (
          <div>
            <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#1f2937', marginBottom: '20px' }}>
              {t('selectCustomer')}
            </h3>
            
            {customersSummary.length > 0 ? (
              <div style={styles.customersList}>
                {customersSummary.map(customer => (
                  <div
                    key={customer.customerId}
                    style={{
                      ...styles.customerCard,
                      ...(selectedCustomer?.customerId === customer.customerId ? styles.customerSelected : {})
                    }}
                    onClick={() => handleCustomerSelect(customer)}
                    onMouseEnter={(e) => {
                      if (selectedCustomer?.customerId !== customer.customerId) {
                        e.currentTarget.style.borderColor = '#c4b5fd';
                        e.currentTarget.style.background = '#f5f3ff';
                        e.currentTarget.style.transform = 'translateY(-2px)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (selectedCustomer?.customerId !== customer.customerId) {
                        e.currentTarget.style.borderColor = '#e5e7eb';
                        e.currentTarget.style.background = 'white';
                        e.currentTarget.style.transform = 'translateY(0)';
                      }
                    }}
                  >
                    <div style={styles.customerInfo}>
                      <div style={styles.customerName}>{customer.name}</div>
                      <div style={styles.customerDetails}>
                        <span>ID: {customer.customerId}</span>
                        <span>•</span>
                        <span>{t('currency')}: {customer.currency}</span>
                        <span>•</span>
                        <span>{t('deposit')}: {customer.deposit?.toLocaleString() || '0'}</span>
                      </div>
                    </div>
                    <div style={styles.customerAmount}>
                      {customer.amount?.toLocaleString() || '0'} {customer.currency}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div style={styles.noData}>
                <User size={48} color="#9ca3af" />
                <p style={{ marginTop: '16px' }}>{t('noCustomersFound')}</p>
              </div>
            )}
          </div>
        );

      case 3:
        return (
          <div>
            <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#1f2937', marginBottom: '20px' }}>
              {selectedCustomer?.name} - {t('selectBookings')} ({selectedBookings.length}/{bookings.length})
            </h3>
            
            {loading ? (
              <div style={styles.loadingContainer}>
                <div style={styles.loadingText}>{t('loadingBookings')}...</div>
              </div>
            ) : bookings.length > 0 ? (
              <>
                <div style={styles.bookingsContainer}>
                  <div style={styles.selectAllContainer}>
                    <input
                      type="checkbox"
                      style={styles.checkbox}
                      checked={selectedBookings.length === bookings.length && bookings.length > 0}
                      onChange={selectAllBookings}
                      id="selectAll"
                    />
                    <label htmlFor="selectAll" style={{ cursor: 'pointer' }}>
                      {t('selectAll')} ({bookings.length} {t('bookings')})
                    </label>
                  </div>
                  
                  {bookings.map(booking => (
                    <div
                      key={`${booking.booking_id}-${booking.rate_id}`}
                      style={{
                        ...styles.bookingItem,
                        ...(selectedBookings.some(b => b.booking_id === booking.booking_id) ? styles.bookingSelected : {})
                      }}
                      onClick={() => toggleBookingSelection(booking)}
                      onMouseEnter={(e) => {
                        if (!selectedBookings.some(b => b.booking_id === booking.booking_id)) {
                          e.currentTarget.style.background = '#f3f4f6';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!selectedBookings.some(b => b.booking_id === booking.booking_id)) {
                          e.currentTarget.style.background = 'white';
                        }
                      }}
                    >
                      <input
                        type="checkbox"
                        style={styles.checkbox}
                        checked={selectedBookings.some(b => b.booking_id === booking.booking_id)}
                        onChange={() => {}}
                        readOnly
                      />
                      <div style={styles.bookingInfo}>
                        <div style={styles.bookingNumber}>
                          {booking.booking_number}
                        </div>
                        <div style={styles.bookingDetails}>
                          <span>{t('service')}: {booking.service}</span>
                          <span>•</span>
                          <span>{t('product')}: {booking.product_name}</span>
                          <span>•</span>
                          <span>{t('checkin')}: {booking.checkin}</span>
                          <span>•</span>
                          <span>{t('checkout')}: {booking.checkout}</span>
                        </div>
                      </div>
                      <div style={styles.bookingAmount}>
                        {booking.amount?.toLocaleString() || '0'} {booking.currency}
                      </div>
                    </div>
                  ))}
                </div>
                
                {selectedBookings.length > 0 && (
                  <div style={styles.summaryCard}>
                    <div style={styles.summaryGrid}>
                      <div style={styles.summaryItem}>
                        <div style={styles.summaryValue}>{selectedBookings.length}</div>
                        <div style={styles.summaryLabel}>{t('selectedBookings')}</div>
                      </div>
                      <div style={styles.summaryItem}>
                        <div style={styles.summaryValue}>
                          {calculateTotalAmount()}
                        </div>
                        <div style={styles.summaryLabel}>
                          {t('totalAmount')} {selectedCustomer?.currency}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div style={styles.noData}>
                <FileText size={48} color="#9ca3af" />
                <p style={{ marginTop: '16px' }}>{t('noBookingsAvailable')}</p>
              </div>
            )}
          </div>
        );

      case 4:
        return (
          <div>
            <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#1f2937', marginBottom: '20px' }}>
              {t('confirmInvoiceCreation')}
            </h3>
            
            <div style={styles.summaryCard}>
              <h4 style={{ marginTop: 0, marginBottom: '16px', color: '#374151' }}>
                {t('summary')}
              </h4>
              
              <div style={{ marginBottom: '20px' }}>
                <div style={{ display: 'flex', marginBottom: '8px' }}>
                  <span style={{ fontWeight: '600', width: '120px' }}>{t('customer')}:</span>
                  <span>{selectedCustomer?.name}</span>
                </div>
                <div style={{ display: 'flex', marginBottom: '8px' }}>
                  <span style={{ fontWeight: '600', width: '120px' }}>{t('currency')}:</span>
                  <span>{selectedCustomer?.currency}</span>
                </div>
                <div style={{ display: 'flex', marginBottom: '8px' }}>
                  <span style={{ fontWeight: '600', width: '120px' }}>{t('selectedBookings')}:</span>
                  <span>{selectedBookings.length}</span>
                </div>
                <div style={{ display: 'flex', marginBottom: '8px' }}>
                  <span style={{ fontWeight: '600', width: '120px' }}>{t('totalAmount')}:</span>
                  <span style={{ fontWeight: '600', color: '#10b981' }}>
                    {calculateTotalAmount()} {selectedCustomer?.currency}
                  </span>
                </div>
              </div>
              
              <div style={styles.dueDateContainer}>
                <label style={styles.dueDateLabel}>
                  <Calendar size={16} />
                  {t('dueDate')}:
                </label>
                <input
                  type="date"
                  style={styles.input}
                  value={dueAt}
                  onChange={(e) => setDueAt(e.target.value)}
                  min={new Date().toISOString().split('T')[0]}
                  required
                />
              </div>
            </div>
            
            <p style={{ color: '#6b7280', fontSize: '14px', lineHeight: '1.6' }}>
              {t('invoiceCreationConfirmation')}
            </p>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div style={styles.modalOverlay} onClick={onClose}>
      <div style={styles.modalContent} onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div style={styles.modalHeader}>
          <h2 style={styles.modalTitle}>
            <FileText size={24} color="#7c3aed" />
            {t('addNewInvoice')}
          </h2>
          <button
            style={styles.closeButton}
            onClick={onClose}
            title={t('close')}
          >
            <X size={20} />
          </button>
        </div>

        {/* Body */}
        <div style={styles.modalBody}>
          {/* Indicateur d'étapes */}
          <div style={styles.stepIndicator}>
            <div style={styles.stepLine}></div>
            <div style={styles.step}>
              <div style={{
                ...styles.stepCircle,
                ...(step >= 1 ? styles.stepActive : styles.stepInactive)
              }}>
                {step > 1 ? <Check size={16} /> : '1'}
              </div>
              <div style={{
                ...styles.stepLabel,
                ...(step >= 1 ? styles.stepLabelActive : {})
              }}>
                {t('filters')}
              </div>
            </div>
            <div style={styles.step}>
              <div style={{
                ...styles.stepCircle,
                ...(step >= 2 ? styles.stepActive : styles.stepInactive)
              }}>
                {step > 2 ? <Check size={16} /> : '2'}
              </div>
              <div style={{
                ...styles.stepLabel,
                ...(step >= 2 ? styles.stepLabelActive : {})
              }}>
                {t('selectCustomer')}
              </div>
            </div>
            <div style={styles.step}>
              <div style={{
                ...styles.stepCircle,
                ...(step >= 3 ? styles.stepActive : styles.stepInactive)
              }}>
                {step > 3 ? <Check size={16} /> : '3'}
              </div>
              <div style={{
                ...styles.stepLabel,
                ...(step >= 3 ? styles.stepLabelActive : {})
              }}>
                {t('selectBookings')}
              </div>
            </div>
            <div style={styles.step}>
              <div style={{
                ...styles.stepCircle,
                ...(step === 4 ? styles.stepActive : styles.stepInactive)
              }}>
                4
              </div>
              <div style={{
                ...styles.stepLabel,
                ...(step === 4 ? styles.stepLabelActive : {})
              }}>
                {t('confirm')}
              </div>
            </div>
          </div>

          {renderStepContent()}
        </div>

        {/* Footer */}
        <div style={styles.modalFooter}>
          {step > 1 && step < 4 && (
            <button
              style={styles.backButton}
              onClick={() => setStep(step - 1)}
              disabled={loading}
            >
              {t('back')}
            </button>
          )}
          
          {step < 3 && (
            <div style={{ flex: 1 }}></div>
          )}
          
          {step === 1 && (
            <button
              style={styles.nextButton}
              onClick={handleSearch}
              disabled={loading}
            >
              {t('next')}
              <ChevronRight size={16} />
            </button>
          )}
          
          {step === 2 && selectedCustomer && (
            <button
              style={styles.nextButton}
              onClick={() => setStep(3)}
            >
              {t('next')}
              <ChevronRight size={16} />
            </button>
          )}
          
          {step === 3 && selectedBookings.length > 0 && (
            <button
              style={styles.nextButton}
              onClick={() => setStep(4)}
            >
              {t('next')}
              <ChevronRight size={16} />
            </button>
          )}
          
          {step === 4 && (
            <button
              style={styles.createButton}
              onClick={handleCreateInvoice}
              disabled={submitting}
            >
              {submitting ? t('creating') : t('createInvoice')}
              {!submitting && <Check size={16} />}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default AddInvoiceModal;